import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { adminAPI } from '../services/api';
import { toast } from '../hooks/use-toast';
import { Loader2, PlusCircle, Pencil } from 'lucide-react';

const emptyForm = {
  title: '', organizer: '', speakers: '', description: '',
  organizerEmail: '', date: '', location: '', categories: [],
  priceRange: '', currency: 'USD', format: 'In-Person',
  websiteUrl: '', ticketUrl: '', coverImage: null
};

const categoryOptions = [
  'Technical SEO', 'Content Strategy', 'Analytics & AI',
  'Local SEO', 'Link Building', 'General Search'
];

const CreateConferenceModal = ({ onSuccess, editData = null }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const isEdit = !!editData;

  useEffect(() => {
    if (open && editData) {
      const d = editData.date ? new Date(editData.date) : null;
      setFormData({
        title: editData.title || '',
        organizer: editData.organizer || '',
        speakers: Array.isArray(editData.speakers) ? editData.speakers.join(', ') : (editData.speakers || ''),
        description: editData.description || '',
        organizerEmail: editData.organizerEmail || '',
        date: d ? d.toISOString().split('T')[0] : '',
        location: editData.location || '',
        categories: editData.categories || [],
        priceRange: editData.priceRange || '',
        currency: editData.currency || 'USD',
        format: editData.format || 'In-Person',
        websiteUrl: editData.websiteUrl || '',
        ticketUrl: editData.ticketUrl || '',
        coverImage: null
      });
    } else if (open) {
      setFormData(emptyForm);
    }
  }, [open, editData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setFormData(prev => ({ ...prev, coverImage: e.target.files[0] }));
  };

  const toggleCategory = (category) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...prev.categories, category]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.organizerEmail || !formData.date) {
      toast({ title: 'Error', description: 'Please fill in all required fields', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      if (isEdit) {
        const speakers = formData.speakers ? formData.speakers.split(',').map(s => s.trim()) : [];
        if (formData.coverImage) {
          // Use FormData when uploading new image
          const submitData = new FormData();
          submitData.append('title', formData.title);
          submitData.append('description', formData.description);
          submitData.append('organizer', formData.organizer);
          submitData.append('speakers', speakers.join(','));
          submitData.append('date', formData.date);
          submitData.append('location', formData.location);
          submitData.append('categories', formData.categories.join(','));
          submitData.append('priceRange', formData.priceRange);
          submitData.append('currency', formData.currency);
          submitData.append('format', formData.format);
          submitData.append('websiteUrl', formData.websiteUrl);
          submitData.append('ticketUrl', formData.ticketUrl);
          submitData.append('organizerEmail', formData.organizerEmail);
          submitData.append('coverImage', formData.coverImage);
          await adminAPI.conferences.updateWithImage(editData.id, submitData);
        } else {
          await adminAPI.conferences.update(editData.id, {
            title: formData.title,
            description: formData.description,
            organizer: formData.organizer,
            speakers,
            date: formData.date,
            location: formData.location,
            categories: formData.categories,
            priceRange: formData.priceRange,
            currency: formData.currency,
            format: formData.format,
            websiteUrl: formData.websiteUrl,
            ticketUrl: formData.ticketUrl,
            organizerEmail: formData.organizerEmail
          });
        }
        toast({ title: 'Updated', description: 'Conference updated successfully' });
      } else {
        const submitData = new FormData();
        submitData.append('title', formData.title);
        submitData.append('description', formData.description);
        submitData.append('organizer', formData.organizer);
        submitData.append('speakers', formData.speakers);
        submitData.append('date', formData.date);
        submitData.append('location', formData.location);
        submitData.append('categories', formData.categories.join(','));
        submitData.append('priceRange', formData.priceRange);
        submitData.append('currency', formData.currency);
        submitData.append('format', formData.format);
        submitData.append('websiteUrl', formData.websiteUrl);
        submitData.append('ticketUrl', formData.ticketUrl);
        submitData.append('organizerEmail', formData.organizerEmail);
        if (formData.coverImage) submitData.append('coverImage', formData.coverImage);
        await adminAPI.conferences.create(submitData);
        toast({ title: 'Created', description: 'Conference created successfully' });
      }
      setOpen(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Operation failed', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {isEdit ? (
          <Button size="sm" variant="ghost" data-testid={`edit-conf-${editData.id}`} className="text-blue-600 hover:text-blue-700 hover:bg-blue-50">
            <Pencil className="w-4 h-4" />
          </Button>
        ) : (
          <Button className="bg-purple-600 hover:bg-purple-700" data-testid="add-conference-btn">
            <PlusCircle className="w-4 h-4 mr-2" /> Add Conference
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Conference' : 'Create New Conference'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label htmlFor="conf-title">Event Title *</Label>
              <Input id="conf-title" name="title" value={formData.title} onChange={handleInputChange} required data-testid="conf-title-input" />
            </div>
            <div>
              <Label htmlFor="conf-organizer">Organizer</Label>
              <Input id="conf-organizer" name="organizer" value={formData.organizer} onChange={handleInputChange} />
            </div>
            <div>
              <Label htmlFor="conf-email">Contact Email *</Label>
              <Input id="conf-email" name="organizerEmail" type="email" value={formData.organizerEmail} onChange={handleInputChange} required />
            </div>
            <div>
              <Label htmlFor="conf-date">Event Date *</Label>
              <Input id="conf-date" name="date" type="date" value={formData.date} onChange={handleInputChange} required />
            </div>
            <div>
              <Label htmlFor="conf-location">Location</Label>
              <Input id="conf-location" name="location" value={formData.location} onChange={handleInputChange} />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="conf-speakers">Speakers (comma separated)</Label>
              <Input id="conf-speakers" name="speakers" value={formData.speakers} onChange={handleInputChange} />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="conf-desc">Description</Label>
              <Textarea id="conf-desc" name="description" value={formData.description} onChange={handleInputChange} rows={3} />
            </div>
            <div className="md:col-span-2">
              <Label>Categories</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {categoryOptions.map(cat => (
                  <Badge key={cat} variant={formData.categories.includes(cat) ? 'default' : 'outline'}
                    className={`cursor-pointer ${formData.categories.includes(cat) ? 'bg-purple-600 text-white' : 'hover:bg-purple-50'}`}
                    onClick={() => toggleCategory(cat)}>{cat}</Badge>
                ))}
              </div>
            </div>
            <div>
              <Label>Format</Label>
              <Select value={formData.format} onValueChange={v => setFormData(prev => ({ ...prev, format: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="In-Person">In-Person</SelectItem>
                  <SelectItem value="Online">Online</SelectItem>
                  <SelectItem value="Hybrid">Hybrid</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Currency</Label>
              <Select value={formData.currency} onValueChange={v => setFormData(prev => ({ ...prev, currency: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="GBP">GBP</SelectItem>
                  <SelectItem value="EUR">EUR</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="conf-price">Price Range</Label>
              <Input id="conf-price" name="priceRange" value={formData.priceRange} onChange={handleInputChange} />
            </div>
            <div>
              <Label htmlFor="conf-website">Website URL</Label>
              <Input id="conf-website" name="websiteUrl" value={formData.websiteUrl} onChange={handleInputChange} />
            </div>
            <div>
              <Label htmlFor="conf-ticket">Ticket URL</Label>
              <Input id="conf-ticket" name="ticketUrl" value={formData.ticketUrl} onChange={handleInputChange} />
            </div>
            <div>
              <Label htmlFor="conf-image">Cover Image</Label>
              {isEdit && editData.coverImage && !formData.coverImage && (
                <div className="mb-2 mt-1">
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/${editData.coverImage}`}
                    alt="Current cover"
                    onError={(e) => { e.target.onerror = null; e.target.src = '/hero-seo.webp'; }}
                    className="w-32 h-20 object-cover rounded border"
                  />
                  <p className="text-xs text-gray-500 mt-1">Current: {editData.coverImage}</p>
                </div>
              )}
              <Input id="conf-image" name="coverImage" type="file" onChange={handleFileChange} accept="image/*" />
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={loading} className="bg-purple-600 hover:bg-purple-700" data-testid="conf-submit-btn">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{isEdit ? 'Saving...' : 'Creating...'}</> : (isEdit ? 'Save Changes' : 'Create Conference')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateConferenceModal;
